type Credentials = {
    email: string;
    password: string;
};

type ApiRequestOptions = {
    body?: unknown;
    headers?: Record< string, string >;
    credentials?: Credentials;
};

type TokenResponse = {
    access_token: string;
    token_type: string;
};

const apiBaseUrl = process.env.REACT_APP_API_URL || '';

class ApiService{
    public credentials?: Credentials;
    public token?: string;

    constructor( credentials?: Credentials ){
        if( credentials )
            this.credentials = credentials;
    }

    async readResponseBody< T, >( response: Response ): Promise< T | null >{
        const contentType = response.headers.get( 'content-type' ) || '';
    
        if( contentType.includes( 'application/json' ) )
            return await response.json();
    
        return null;
    };
    
    async readErrorMessage( response: Response ){
        const data = await this.readResponseBody< { detail?: string } >( response );
    
        if( data?.detail )
            return data.detail;
    
        return response.statusText || 'Request failed.';
    };
    
    async validateToken( token: string ){
        if( ! token )
            return false;

        try{
            const response = await fetch(
                `${apiBaseUrl}/api/v1/auth/validate-token`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            return response.ok;
        }catch( err ){
            return false;
        }
    };

    async login() {
        const response = await fetch(
            `${apiBaseUrl}/api/v1/auth/login`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify( this.credentials )
            }
        );

        if( ! response.ok )
            return null;

        const data = await response.json() as TokenResponse;

        if( data?.access_token ){
            this.token = data.access_token;
            return true;
        }

        return false;
    };

    async ensureValidToken() {
        if( ! this.token )
            return await this.login();

        return await this.validateToken( this.token );
    };

    async request< T, >( method: string, path: string, options: ApiRequestOptions = {} ) {
        await this.ensureValidToken();

        if( ! this.token )
            throw new Error( 'Please log in to continue.' );

        const headers: Record<string, string> = {
            ...( options.headers || {} ),
            Authorization: `Bearer ${this.token}`
        };

        let body: BodyInit | undefined;

        if( options.body !== undefined ){
            if( options.body instanceof FormData ){
                body = options.body;
            }else{
                if( ! headers[ 'Content-Type' ] )
                    headers[ 'Content-Type' ] = 'application/json';

                body = typeof options.body === 'string' ? options.body : JSON.stringify( options.body );
            }
        }

        let response = await fetch(
            `${apiBaseUrl}${path}`,
            {
                method,
                headers,
                body
            }
        );

        if( ! response.ok )
            throw new Error( await this.readErrorMessage( response ) );

        return await this.readResponseBody<T>( response );
    };
}

export default ApiService;
export type { Credentials };